import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Menu, X, Feather } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, [setIsScrolled]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Books', path: '/books' },
    { name: 'News', path: '/news' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  // Scroll to top when clicking home link
  const handleHomeClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  // Helper function for link styling
  const getLinkClassName = (path) => {
    const baseClasses = 'text-base font-medium transition-colors relative group';
    
    if (isScrolled) {
      return `${baseClasses} ${isActive(path) ? 'text-amber-600' : 'text-slate-700 hover:text-amber-600'}`;
    }
    return `${baseClasses} ${isActive(path) ? 'text-amber-400' : 'text-white hover:text-amber-400'}`;
  };

  const getIconColor = () => isScrolled ? 'text-slate-900' : 'text-white';
  const getUnderlineColor = () => isScrolled ? 'bg-amber-600' : 'bg-amber-400';

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-md py-4' 
            : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" onClick={handleHomeClick}>
              <Feather 
                className={`w-8 h-8 transition-colors ${
                  isScrolled ? 'text-amber-600' : 'text-white'
                } group-hover:text-amber-500`} 
              />
              <span 
                className={`text-2xl font-serif font-bold transition-colors ${
                  isScrolled ? 'text-slate-900' : 'text-white'
                } group-hover:text-amber-500`}
              >
                Laura Novak
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={getLinkClassName(link.path)}
                  onClick={link.path === '/' ? handleHomeClick : undefined}
                >
                  {link.name}
                  <span 
                    className={`absolute -bottom-1 left-0 w-0 h-0.5 transition-all group-hover:w-full ${getUnderlineColor()} ${isActive(link.path) ? 'w-full' : ''}`}
                  ></span>
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 z-50 relative"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-7 h-7 text-white" />
              ) : (
                <Menu className={`w-7 h-7 ${getIconColor()}`} />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
          style={{ animation: 'fadeIn 0.3s ease-out' }}
        />
      )}

      {/* Mobile Navigation Drawer */}
      <nav 
        className={`fixed top-0 right-0 h-full w-[80%] max-w-sm bg-slate-900 z-40 md:hidden transform transition-transform duration-300 ease-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ 
          boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.3)',
          borderLeft: '2px solid rgb(251, 191, 36)'
        }}
      >
        {/* Menu Header */}
        <div className="px-8 pt-24 pb-8 border-b border-slate-800">
          <div className="flex items-center gap-3 mb-2">
            <Feather className="w-6 h-6 text-amber-400" />
            <span className="text-xl font-serif font-bold text-white">
              Laura Novak
            </span>
          </div>
          <p className="text-sm text-slate-400">Author & Journalist</p>
        </div>

        {/* Menu Items */}
        <div className="px-8 py-6 space-y-1">
          {navLinks.map((link, index) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={link.path === '/' ? handleHomeClick : () => setIsMobileMenuOpen(false)}
              className={`
                flex items-center gap-3 py-4 px-4 rounded-lg
                text-lg font-medium transition-all duration-200
                ${isActive(link.path) 
                  ? 'bg-amber-500/10 text-amber-400 border-l-4 border-amber-400' 
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white border-l-4 border-transparent'
                }
              `}
              style={{ 
                animation: `slideInRight 0.3s ease-out ${index * 0.05}s both`
              }}
            >
              {isActive(link.path) && (
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
              )}
              {link.name}
            </Link>
          ))}
        </div>

        {/* Menu Footer */}
        <div className="absolute bottom-0 left-0 right-0 px-8 py-6 border-t border-slate-800">
          <p className="text-xs text-slate-500 text-center">
            © 2026 Laura Novak. All rights reserved.
          </p>
        </div>
      </nav>

      {/* Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
};

export default Header;